import React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, TableBody, TableCell, TableContainer, TableRow, TextField, Table, Alert, Chip, Autocomplete, IconButton, Skeleton, TableHead } from '@mui/material';
import { Grid, makeStyles, Typography } from '@material-ui/core';
import MissionSummaryService from '../../../services/MissionSummaryService';
import { useNavigate, useParams, useLocation } from 'react-router';
import Message from '../../tools/Message';
import LoadingSimpleBackdrop from '../../tools/LoadingSimpleBackdrop';
import AddIcon from '@mui/icons-material/Add';
import TimeToLeaveIcon from '@mui/icons-material/TimeToLeave';
import CardTravelIcon from '@mui/icons-material/CardTravel';
import CloseIcon from '@mui/icons-material/Close';
import { TabContext, TabPanel } from '@mui/lab';
import DestinationDialog from './DestinationAddDialog';
import DestinationViewDialog from './DestinationViewDialog';
import RemoveDestinationDialog from './RemoveDestinationDialog';
import { validateTimesheetForm } from '../../../helpers/ValidateForm';
import RentManagement from './RentManagement';
import { formatDate } from '../../../helpers/FormateDate';
import formatWithThousandSeparator from '../../../helpers/FormatWithThousandSeparator';
import { formatArrivee, formatDepart, isEmpty } from '../../../helpers/MissionHelper';
import {
  countNightsMission,
  distributeNightNumbers,
  getNightDateKey,
  getNightDistributionMeta,
  normalizeNightInputValue,
  normalizeNightNumber,
} from '../../../helpers/TimesheetNightHelper';
import { obtenirIndicesMontantsJournaliersMasques } from '../../../helpers/TimesheetRentHelper';
import TimeSheetMissionService from '../../../services/TimeSheetMissionService';
import CarRentalIcon from '@mui/icons-material/CarRental';
import MissionRentService from '../../../services/MissionRentService';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import MissionAssignVehicle from '../logistic/MissionAssignVehicle';
import MissionVehicleSolicitation from '../logistic/MissionVehicleSolicitation';
import TimesheetRefuse from './TimesheetRefuse';
import ConfirmationTransport from '../logistic/ConfirmationTransport';
import CheckBoxTimesheet, { useTimesheetCheckboxSelection } from './checkBoxTimesheet';
import timesheetValidateuseStyles from '../../../styles/TimesheetValidate.style';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Bullred from '../../../components/reusable/Bullred';
import { stickyHeaderCellSx, stickySubHeaderCellSx } from '../../../styles/TableStyle.style';
import EditIcon from '@mui/icons-material/Edit';

const normalizeTransportTypeForAssignment = (
  transportType,
  missionRentId = null,
  rentType = null
) => {
  const rawType = missionRentId ? rentType || transportType : transportType;
  const normalizedType = (rawType || '').toString().trim().toUpperCase();

  if (missionRentId) {
    return rawType || 'LOCATION';
  }
  if (!normalizedType || ['FID', 'VOITURE', 'VOITURE FID'].includes(normalizedType)) {
    return 'FID';
  }
  if (['AVION', 'BATEAU'].includes(normalizedType)) {
    return normalizedType;
  }
  return rawType;
};

const convertirEnNombre = (valeur, valeurParDefaut = 0) => {
  const nombre = Number.parseFloat(String(valeur ?? '').replace(',', '.'));
  return Number.isFinite(nombre) ? nombre : valeurParDefaut;
};

const valeurEstRenseignee = (valeur) =>
  valeur !== null && valeur !== undefined && valeur !== '';

const premiereValeurRenseignee = (...valeurs) =>
  valeurs.find(valeurEstRenseignee);

const arrondirALaCentaine = (montant) =>
  Math.round(convertirEnNombre(montant, 0) / 100) * 100;

const obtenirIdLocationDestination = (destination = {}) => {
  const transportLocation = (Array.isArray(destination?.transports)
    ? destination.transports
    : []
  ).find((transport) => valeurEstRenseignee(transport?.mission_rent_id));

  return premiereValeurRenseignee(
    transportLocation?.mission_rent_id,
    destination?.mission_rent_id
  ) ?? null;
};

const obtenirInformationsLocation = (destination = {}, locations = new Map()) => {
  const transports = Array.isArray(destination?.transports)
    ? destination.transports
    : [];
  const transportPrioritaire =
    transports.find((transport) => transport?.mission_rent_id) ||
    transports[0] ||
    {};
  const missionRentId = premiereValeurRenseignee(
    transportPrioritaire?.mission_rent_id,
    destination?.mission_rent_id
  );
  const locationReference = missionRentId
    ? locations.get(String(missionRentId)) || {}
    : {};

  return {
    mission_rent_id: missionRentId ?? null,
    rent_type: premiereValeurRenseignee(
      transportPrioritaire?.rent_type,
      destination?.rent_type,
      locationReference?.rent_type,
      locationReference?.label
    ),
    rent_amount: premiereValeurRenseignee(
      transportPrioritaire?.rent_amount,
      destination?.rent_amount,
      locationReference?.rent_amount
    ),
    rent_paiement: premiereValeurRenseignee(
      transportPrioritaire?.rent_paiement,
      destination?.rent_paiement,
      locationReference?.rent_paiement
    ),
    rent_upfuel: premiereValeurRenseignee(
      transportPrioritaire?.rent_upfuel,
      destination?.rent_upfuel,
      locationReference?.rent_upfuel
    ),
    rent_conso_100km: premiereValeurRenseignee(
      transportPrioritaire?.rent_conso_100km,
      destination?.rent_conso_100km,
      locationReference?.rent_conso_100km
    ),
  };
};

const obtenirTransportsAvecVehicule = (destination = {}) =>
  (Array.isArray(destination?.transports) ? destination.transports : []).filter(
    (transport) =>
      valeurEstRenseignee(transport?.transport_id) &&
      valeurEstRenseignee(transport?.vhc_id)
  );

const calculerDetailsCarburantTransport = (transport = {}, destination = {}) => {
  const kilometrage = convertirEnNombre(
    destination?.mission_timesheet_details_km ?? destination?.distance_km,
    0
  );
  const consommationPourCent = convertirEnNombre(transport?.vhc_conso_100km, 0);
  const consommationEnregistree = convertirEnNombre(
    transport?.mission_timesheet_details_consumption,
    0
  );
  const consommation = consommationPourCent > 0
    ? (kilometrage * consommationPourCent) / 100
    : consommationEnregistree;
  const estLocation = valeurEstRenseignee(
    transport?.mission_rent_id ?? destination?.mission_rent_id
  );
  const prixUnitaire = convertirEnNombre(
    estLocation
      ? premiereValeurRenseignee(
          transport?.rent_upfuel,
          destination?.rent_upfuel,
          transport?.price_per_liter
        )
      : transport?.price_per_liter,
    0
  );
  const montantEnregistre = convertirEnNombre(
    transport?.mission_timesheet_details_amount,
    0
  );
  const montantBrut = consommation > 0 && prixUnitaire > 0
    ? consommation * prixUnitaire
    : montantEnregistre;

  return {
    kilometrage,
    consommation,
    prixUnitaire,
    montantCarburant: arrondirALaCentaine(montantBrut),
  };
};

const calculerDetailsCarburantSansVehicule = (
  destination = {},
  informationsLocation = {}
) => {
  const kilometrage = convertirEnNombre(
    destination?.mission_timesheet_details_km ?? destination?.distance_km,
    0
  );
  const consommationEnregistree = convertirEnNombre(
    destination?.mission_timesheet_details_consumption,
    0
  );
  const consommationVehicule = convertirEnNombre(destination?.vhc_conso_100km, 0);
  const consommation = informationsLocation?.mission_rent_id
    ? consommationEnregistree
    : consommationVehicule > 0
      ? (kilometrage * consommationVehicule) / 100
      : consommationEnregistree;
  const montantEnregistre = convertirEnNombre(
    destination?.mission_timesheet_details_amount,
    0
  );
  const estLocation = valeurEstRenseignee(informationsLocation?.mission_rent_id);
  const prixUnitaire = estLocation
    ? convertirEnNombre(informationsLocation?.rent_upfuel, 0)
    : consommation > 0 && montantEnregistre > 0
      ? montantEnregistre / consommation
      : 0;

  return {
    kilometrage,
    consommation,
    prixUnitaire,
    montantCarburant: arrondirALaCentaine(montantEnregistre),
  };
};

const calculerDetailsCarburantDestination = (
  destination = {},
  locations = new Map()
) => {
  const informationsLocation = obtenirInformationsLocation(destination, locations);
  const transports = obtenirTransportsAvecVehicule(destination);

  if (transports.length === 0) {
    return {
      informationsLocation,
      detailsDestination: calculerDetailsCarburantSansVehicule(
        destination,
        informationsLocation
      ),
      detailsTransports: [],
    };
  }

  const detailsTransports = transports.map((transport) => ({
    transport,
    details: calculerDetailsCarburantTransport(transport, destination),
  }));
  const consommation = detailsTransports.reduce(
    (total, ligne) => total + ligne.details.consommation,
    0
  );
  const montantCarburant = detailsTransports.reduce(
    (total, ligne) => total + ligne.details.montantCarburant,
    0
  );

  return {
    informationsLocation,
    detailsTransports,
    detailsDestination: {
      kilometrage: convertirEnNombre(
        destination?.mission_timesheet_details_km ?? destination?.distance_km,
        0
      ),
      consommation,
      prixUnitaire: consommation > 0 ? montantCarburant / consommation : 0,
      montantCarburant,
    },
  };
};

const TimesheetValidate = () => {
  const { mission_planning_id, mission_timesheet_id, agenda_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const routeState = location.state || {};
  const searchParams = new URLSearchParams(location.search);
  const [missionTimesheetState, setMissionTimesheetState] = useState(
    routeState.mission_timesheet_state || searchParams.get('mission_timesheet_state') || ''
  );
  const [missionTimesheetUserId, setMissionTimesheetUserId] = useState(
    routeState.mission_timesheet_user_id ?? searchParams.get('mission_timesheet_user_id') ?? null
  );
  const openTransportDialog = Boolean(
    routeState.openTransportDialog || searchParams.get('openTransportDialog') === '1'
  );
  const openSolicitationDialog = Boolean(
    routeState.openSolicitationDialog || searchParams.get('openSolicitationDialog') === '1'
  );
  const classes = timesheetValidateuseStyles();
  const [mission, setMission] = useState(null);
  const [loadingBackdrop, setLoadingBackdrop] = useState(false);
  const [loadingDestinations, setLoadingDestinations] = useState(false);
  const [agendas, setAgendas] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [value, setValue] = useState('1');
  const [missionnaries, setMissionnaries] = useState([]);
  const [loadingButton, setLoadingButton] = useState(false);
  const [dialogTransport, setDialogTransport] = useState({
    open: false,
    agenda_id: null,
    isEdit: false,
    selectedDestinations: [],
    groupedDestinations: [],
  });
  const [autoTransportDialogOpened, setAutoTransportDialogOpened] = useState(false);
  const [autoSolicitationDialogOpened, setAutoSolicitationDialogOpened] = useState(false);
  const [dialogSolicitation, setDialogSolicitation] = useState({
    open: false,
    selectedDestinations: [],
  });
  const [message, setMessage] = useState({
      open: false,
      color: 'green',
      messageInfo: '',
    });
    const [dialogRefus, setDialogRefus] = useState({
      open: false,
      timesheetId: null,
    });
    const steps = ['Validatiion et Generation Timesheet', "Generation de l' OM du Chauffeur ", 'Generation de la decompte Carburant previsionnel'];
  // Sélection des lignes via checkbox
  const [selectionWarningDialog, setSelectionWarningDialog] = useState({
    open: false,
    message: '',
  });
  const {
    selectedKeys,
    resetSelection,
    buildSelectedDestinationDetails,
    getHeaderCheckboxProps,
    getRowCheckboxProps,
  } = useTimesheetCheckboxSelection({
    destinations,
    missionTimesheetState: missionTimesheetState,
  });
  const missionnaryIds = useMemo(
    () => new Set((missionnaries || []).map((missionnary) => Number(missionnary.user_id))),
    [missionnaries]
  );
  const rentOccurrencesById = useMemo(
    () => destinations.reduce((acc, destination) => {
      const rentId = obtenirIdLocationDestination(destination);
      if (rentId === null || rentId === undefined) {
        return acc;
      }

      acc[rentId] = (acc[rentId] || 0) + 1;
      return acc;
    }, {}),
    [destinations]
  );
  const nightDistributionMeta = getNightDistributionMeta(destinations);
  const canManageTransport = !['CONFIRMED', 'REFUSED'].includes(missionTimesheetState);
  const tableSkeletonColumns = canManageTransport ? 27 : 26;
  const totalLabelColumnSpan = canManageTransport ? 16 : 15;
  const [prevConfirmDialog, setPrevConfirmDialog] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    mission_planning_id: mission_planning_id || null,
    agenda_id: agenda_id || null,
   mission_timesheet_id: mission_timesheet_id || null,
  });
  const [transportMeans, setTransportMeans] = useState( [
    { label: 'Voiture FID', value: 'FID', mission_rent_id: null, rent_upfuel: null, rent_amount: null, rent_paiement: null, rent_conso_100km: null },
    { label: 'Avion', value: 'AVION', mission_rent_id: null, rent_upfuel: null, rent_amount: null, rent_paiement: null, rent_conso_100km: null },
    { label: 'Bateau', value: 'BATEAU', mission_rent_id: null, rent_upfuel: null, rent_amount: null, rent_paiement: null, rent_conso_100km: null },
  ]);
  const locationsParId = useMemo(
    () => new Map(
      transportMeans
        .filter((moyen) => moyen?.mission_rent_id)
        .map((moyen) => [String(moyen.mission_rent_id), moyen])
    ),
    [transportMeans]
  );
  const indicesMontantsJournaliersMasques = useMemo(
    () => obtenirIndicesMontantsJournaliersMasques(
      destinations,
      (destination) => obtenirInformationsLocation(destination, locationsParId)
    ),
    [destinations, locationsParId]
  );
  const [destDialog, setDestDialog] = useState({
    open: false,
    agenda_id: null,
    mission_timesheet_id: null,
    onClose: () => setDestDialog((prev) => ({
      ...prev,
      open: false,
      agenda_id: null,
      mission_timesheet_id: null,
    })),
    missionnaries: [],
  });
  const [removeDialog, setRemoveDialog] = useState({ open: false, agenda_id: null, mission_destination_id: null, mission_timesheet_destination_id: null, rowIndex: null, onClose: () => setRemoveDialog({ ...removeDialog, open: false, agenda_id: null, mission_destination_id: null, mission_timesheet_destination_id: null, rowIndex: null }) });
  const [viewDialog, setViewDialog] = useState({ open: false, destination: null, index: null, editable: false });
  const { open, color, messageInfo } = message;
   
 function handleCloseMessage() {
    setMessage({ ...message, open: false });
  }

  const fetchMission = async () => {
    try {
      setLoadingBackdrop(true);
      const response = await MissionSummaryService.getMissionData(mission_planning_id, agenda_id);
      if(response && response.status){
        setMission({...response.data, mission_timesheet_user_id : response.data.timesheet_user_id || null, mission_planning_agenda_id: agenda_id || null });
        setAgendas(response.data.agendas || []);
        setMissionnaries(response.data.missionnaries || []);
        if (!missionTimesheetUserId && response.data?.timesheet_user_id) {
          setMissionTimesheetUserId(response.data.timesheet_user_id);
        }
        if (!missionTimesheetState && response.data?.mission_timesheet_state) {
          setMissionTimesheetState(response.data.mission_timesheet_state);
        }
      }
    }
    catch (error) {
      console.error("Error fetching mission data:", error);
    }
    finally {
      setLoadingBackdrop(false);
    }
  };

  const fetchDestinations = async () => { // recuperer les lignes d'agenda pour le timesheet
    try {
      setLoadingBackdrop(true);
      setLoadingDestinations(true);
      const response = await TimeSheetMissionService.retrieveDestinationsWithTransportTimesheet(mission_planning_id, agenda_id,mission_timesheet_id);
      if(response && response.status){
        resetSelection();
        setDestinations(normalizeDestinations(response.data || []));
        if (!missionTimesheetState && Array.isArray(response.data)) {
          const stateFromDestinations = response.data.find((dest) => dest?.mission_timesheet_state)?.mission_timesheet_state;
          if (stateFromDestinations) {
            setMissionTimesheetState(stateFromDestinations);
          }
        }
      }
    }
    catch (error) {
      console.error("Error fetching destinations data:", error);
    }
    finally {
      setLoadingDestinations(false);
      setLoadingBackdrop(false);
    }
  };

  const checkKMGap = (destination = {}) => {
    const km = Number(destination?.mission_timesheet_details_km ?? destination?.distance_km ?? 0);
    const kmSuggestion = Number(destination?.km_suggestion ?? 0);
    if (!Number.isFinite(km) || !Number.isFinite(kmSuggestion) || kmSuggestion <= 0) {
      return 0;
    }
    return Math.abs(km - kmSuggestion);
  };

  const withKmGapState = (destination = {}) => { // vérifier l'ecart du kilometrage saisi avec la suggestion (si l'ecart est supperieur à 50km alors on affiche un warning dans la ligne)
    const kmGap = checkKMGap(destination);
    return {
      ...destination,
      kmGap,
      kmGapExceeded: kmGap > 50,
    };
  };

  const normalizeDestinations = (items = []) => { // normaliser les données des destinations pour l'affichage (ajout du transport_type au niveau de la destination, calcul du nombre de nuit si non renseigné, ajout de l'état de l'écart du km avec la suggestion)
    const normalizedItems = items.map((dest, index) => {
      const previousDateEnd = index > 0 ? items[index - 1].date_end : null;
      const informationsLocation = obtenirInformationsLocation(dest, locationsParId);
      const transport_type = normalizeTransportTypeForAssignment(
        dest.transport_type,
        informationsLocation.mission_rent_id,
        informationsLocation.rent_type
      );
      const hasSavedNightNumber =
        dest.night_number !== undefined && dest.night_number !== null;
      const normalizedDestination = {
        ...dest,
        ...informationsLocation,
        transport_type,
        night_number: hasSavedNightNumber
          ? dest.night_number
          : countNightsMission(previousDateEnd, dest.date_begin, dest.date_end),
        _nightNumberSource: hasSavedNightNumber ? 'saved' : 'automatic',
      };
      return withKmGapState(normalizedDestination);
    });
    return distributeNightNumbers(normalizedItems);
  };

  useEffect(() => { // données misison
    fetchMission();
  }, [mission_planning_id]);

  useEffect(() => {
    fetchDestinations(); // lignes du timesheet
    fetchTransportMeans(); // moyens de transport disponibles
  }, [mission_planning_id, agenda_id,mission_timesheet_id]);

const handleChangeKm = (e, index) => {
  const kmValue = convertirEnNombre(e.target.value, 0);
  setDestinations((prev = []) => {
    const updated = [...prev];
    const current = withKmGapState({
      ...(updated[index] || {}),
      mission_timesheet_details_km: kmValue,
      distance_km: kmValue,
    });
    const estCovoiturageExterne = isExternalCarpoolingDestination(current);
    const { detailsDestination } = calculerDetailsCarburantDestination(
      current,
      locationsParId
    );

    updated[index] = withKmGapState({
      ...current,
      mission_timesheet_details_consumption: estCovoiturageExterne
        ? null
        : detailsDestination.consommation,
      mission_timesheet_details_amount: estCovoiturageExterne
        ? null
        : detailsDestination.montantCarburant,
    });
    return updated;
  });
};

const handleChangeNightNumber = (value, index) => {
  setDestinations((previousDestinations = []) => {
    const updatedDestinations = [...previousDestinations];
    updatedDestinations[index] = {
      ...(updatedDestinations[index] || {}),
      night_number: normalizeNightInputValue(value),
      _nightNumberSource: 'manual',
    };
    return updatedDestinations;
  });
};

const handleBlurNightNumber = (index) => {
  setDestinations((previousDestinations = []) => {
    const updatedDestinations = [...previousDestinations];
    updatedDestinations[index] = {
      ...(updatedDestinations[index] || {}),
      night_number: normalizeNightNumber(updatedDestinations[index]?.night_number),
    };
    return distributeNightNumbers(updatedDestinations);
  });
};

const setDestinationsWithNightDistribution = (updater) => {
  setDestinations((previousDestinations) => {
    const nextDestinations = typeof updater === 'function'
      ? updater(previousDestinations)
      : updater;
    return distributeNightNumbers(nextDestinations || []);
  });
};

const handleCloseDestDialog = () => {
    setDestDialog((prev) => ({
      ...prev,
      open: false,
      agenda_id: null,
      mission_timesheet_id: null,
    }));
  };

const handleOpenDestDialog = (agenda_id) => {
    setDestDialog({
      open: true,
      agenda_id: agenda_id,
      mission_timesheet_id: mission_timesheet_id,
      onClose: handleCloseDestDialog,
      missionnaries: missionnaries
    });
  }

  const handleCloseDialog = () => {
    navigate('/Logistic/mission/transport_managment');
    setDestinations([]);
    resetSelection();
  };
   const fetchTransportMeans = async () => { // récuperer les differents moyns de transport
          try {
              const result = await MissionRentService.getRentsByAgendaId(agenda_id);
              if(!result || !result.status) {
                  console.error("Error fetching transport:", result ? result.message : 'Unknown error');
                  return;
              }
            const rentsResult = result.data;
              setTransportMeans(prev => {
                const transportOptions = rentsResult.map(rent => ({
                  label: rent.rent_type,
                  value: rent.rent_type,
                  mission_rent_id: rent.mission_rent_id,
                  rent_upfuel: rent.rent_upfuel,
                  rent_amount: rent.rent_amount,
                  rent_paiement: rent.rent_paiement,
                  rent_conso_100km: rent.rent_conso_100km,
                }));
                return [  ...prev, ...transportOptions ];
            });

          } catch (error) {
              console.error("Error fetching transport:", error);
          }
  };

  // Clé composée d'une ligne (valeur des checkbox)
  const groupDestinationsByTransportType = (items = []) => {
    if (!Array.isArray(items) || !items.length) {
      return [];
    }
    const grouped = new Map();
    items.forEach((item) => {
      const missionRentId = obtenirIdLocationDestination(item);
      const transportType = normalizeTransportTypeForAssignment(
        item?.transport_type,
        missionRentId,
        item?.rent_type
      );
      const groupKey = missionRentId
        ? `rent:${missionRentId}`
        : `type:${transportType}`;
      const existingGroup = grouped.get(groupKey) || {
        transport_type: transportType,
        mission_rent_id: missionRentId,
        destinations: [],
      };
      existingGroup.destinations.push({
        ...item,
        transport_type: transportType,
      });
      grouped.set(groupKey, existingGroup);
    });

    return Array.from(grouped.values());
  };

  const hasAssignedTransport = (destination = {}) =>
    Boolean(destination?.mission_rent_id) ||
    (Array.isArray(destination?.transports) && destination.transports.length > 0) ||
    destination?.transport_id;

  const buildDestinationDetails = (items = []) =>
    (items || []).map((destination) => {
      const informationsLocation = obtenirInformationsLocation(destination, locationsParId);
      return {
        mission_destination_id: destination?.mission_destination_id ?? null,
        mission_timesheet_destination_id: destination?.mission_timesheet_destination_id ?? null,
        transport_type: normalizeTransportTypeForAssignment(
          destination?.transport_type,
          informationsLocation.mission_rent_id,
          informationsLocation.rent_type
        ),
        mission_rent_id: informationsLocation.mission_rent_id,
        transports: destination?.transports || [],
        date_begin: destination?.date_begin || null,
        date_end: destination?.date_end || null,
        missionnaries: destination?.missionnaries || [],
      };
    });

  const handleChangeFormValues = (field, value, index) => {
    if (field === 'transport_type') {
      const transportValue = value?.value ?? '';
      const mission_rent_id = value?.mission_rent_id ?? null;

      setDestinations((prev = []) => {
        const updated = [...prev];
        const currentEntry = { ...(updated[index] || {}) };
        const currentKm = Number(currentEntry.mission_timesheet_details_km) || 0;
        const rentDetails = mission_rent_id ? transportMeans.find(
          (transport) => String(transport?.mission_rent_id) === String(mission_rent_id)
        ) : null;
        const rentConso = rentDetails?.rent_conso_100km || 0;
        const rentUpFuel = rentDetails?.rent_upfuel || 0;
        const mission_timesheet_details_consumption = mission_rent_id && rentConso ? currentKm / rentConso : 0;
        const mission_timesheet_details_amount = mission_rent_id ? arrondirALaCentaine(mission_timesheet_details_consumption * rentUpFuel) : 0;

        updated[index] = withKmGapState({
          ...currentEntry,
          mission_rent_id: mission_rent_id,
          rent_type: rentDetails?.rent_type ?? rentDetails?.label ?? null,
          rent_amount: rentDetails?.rent_amount ?? null,
          rent_paiement: rentDetails?.rent_paiement ?? null,
          rent_upfuel: rentDetails?.rent_upfuel ?? null,
          rent_conso_100km: rentDetails?.rent_conso_100km ?? null,
          [field]: transportValue,
          mission_timesheet_details_consumption,
          mission_timesheet_details_amount,
        });
        return updated;
      });
      return;
    }

    setDestinations((prev = []) => {
      const updated = [...prev];
      updated[index] = withKmGapState({
        ...(updated[index] || {}),
        [field]: value,
      });
      
      // calculer montant et consommation si le moyen de transport est une location
      if (updated[index].mission_rent_id !== null && updated[index].mission_rent_id !== undefined) {
        const rent_upfuel = transportMeans.find(
          (transport) => String(transport?.mission_rent_id) === String(updated[index].mission_rent_id)
        )?.rent_upfuel || 0;
        if (field === 'mission_timesheet_details_consumption') {
          updated[index].mission_timesheet_details_amount = arrondirALaCentaine(rent_upfuel * value);
        } else if (field === 'mission_timesheet_details_amount') {
          updated[index].mission_timesheet_details_consumption = rent_upfuel ? value / rent_upfuel : 0;
        }
        updated[index] = withKmGapState(updated[index]);
      }
      return updated;
    });
  };

  const getTransportCaptainIds = (destination = {}) => {
    if (
      destination?.transport_type !== 'FID' ||
      destination?.mission_rent_id ||
      !Array.isArray(destination?.transports) ||
      destination.transports.length === 0
    ) {
      return [];
    }

    return destination.transports
      .map((transport) => transport?.captain_id)
      .filter((captainId) => captainId !== null && captainId !== undefined);
  };

  const isExternalCarpoolingDestination = (destination = {}) => {
    const transportCaptainIds = getTransportCaptainIds(destination);
    if (!transportCaptainIds.length) {
      return false;
    }
    return transportCaptainIds.every((captainId) => !missionnaryIds.has(Number(captainId)));
  };


  const enrichirDestinationPourEnregistrement = (destination = {}) => {
    const estCovoiturageExterne = isExternalCarpoolingDestination(destination);
    if (estCovoiturageExterne) {
      return {
        ...destination,
        mission_timesheet_details_consumption: null,
        mission_timesheet_details_amount: null,
      };
    }

    const {
      informationsLocation,
      detailsDestination,
      detailsTransports,
    } = calculerDetailsCarburantDestination(destination, locationsParId);
    const detailsParTransport = new Map(
      detailsTransports.map(({ transport, details }) => [
        String(transport.transport_id),
        details,
      ])
    );
    const transports = (
      Array.isArray(destination?.transports) ? destination.transports : []
    ).map((transport) => {
      const details = detailsParTransport.get(String(transport?.transport_id));
      if (!details) {
        return transport;
      }

      return {
        ...transport,
        mission_timesheet_details_consumption: details.consommation,
        mission_timesheet_details_amount: details.montantCarburant,
        price_per_liter: details.prixUnitaire,
      };
    });

    return {
      ...destination,
      transports,
      mission_rent_id: informationsLocation.mission_rent_id,
      rent_type: informationsLocation.rent_type,
      rent_amount: informationsLocation.rent_amount,
      rent_paiement: informationsLocation.rent_paiement,
      rent_upfuel: informationsLocation.rent_upfuel,
      mission_timesheet_details_consumption: detailsDestination.consommation,
      mission_timesheet_details_amount: detailsDestination.montantCarburant,
    };
  };

  const buildTimesheetPayload = (
    destinationRows = destinations,
    targetTimesheetId = mission_timesheet_id
  ) => { // construire le payload pour l'enregistrement de la feuille de temps
    const normalizedRows = distributeNightNumbers(destinationRows);

    return {
      mission_timesheet_id: Number(targetTimesheetId) > 0 ? targetTimesheetId : null,
      mission_planning_agenda_id: agenda_id || null,
      mission_planning_id: mission_planning_id || mission?.mission_planning_id || null,
      mission_timesheet_state: missionTimesheetState,
      action: Number(targetTimesheetId) > 0 ? 'update' : 'create',
      timesheet_destinations: normalizedRows.map((destination) => {
        const destinationForPayload = { ...destination };
        delete destinationForPayload._nightNumberSource;
        return enrichirDestinationPourEnregistrement({
          ...destinationForPayload,
          night_number: destination.night_number ?? 0,
        })
      }),
    };
  };

  const handleAddDestination = async (newDestination) => {
    const selectedMissionaryIds = (newDestination?.missionnaries || []).map(
      (missionnary) => missionnary?.missionary_id
        ?? missionnary?.user_id
        ?? missionnary
    );
    const draftDestination = {
      ...newDestination,
      mission_timesheet_id: null,
      mission_timesheet_destination_id: null,
      distance_km: newDestination?.distance_km ?? null,
      night_number: newDestination?.night_number || 0,
      _nightNumberSource: 'automatic',
      missionnaries: missionnaries.filter((missionnary) =>
        selectedMissionaryIds.some(
          (id) => String(id) === String(missionnary.user_id)
        )
      ),
    };
    const draftDestinations = normalizeDestinations([
      ...destinations,
      draftDestination,
    ]);
    const dataToSend = {
      ...buildTimesheetPayload(draftDestinations, null),
      mission_timesheet_state: missionTimesheetState || 'PLANNING',
    };
    const validation = validateTimesheetForm(dataToSend);

    if (!validation.valid) {
      setMessage({ open: true, color: 'red', messageInfo: validation.message });
      return false;
    }

    if (!/\/0\/?$/.test(location.pathname)) {
      setMessage({
        open: true,
        color: 'red',
        messageInfo: "Impossible de déterminer l'adresse du nouveau brouillon.",
      });
      return false;
    }

    setLoadingBackdrop(true);
    setDisabled(true);
    try {
      const result = await TimeSheetMissionService.saveTimesheetMission(dataToSend);
      const newTimesheetId = Number(result?.data?.mission_timesheet_id);

      if (!result?.status || !Number.isInteger(newTimesheetId) || newTimesheetId <= 0) {
        setMessage({
          open: true,
          color: 'red',
          messageInfo: result?.message || 'Erreur lors de la création du brouillon.',
        });
        return false;
      }

      const nextPath = location.pathname.replace(/\/0\/?$/, `/${newTimesheetId}`);
      setDestinations(draftDestinations);
      setMessage({
        open: true,
        color: 'green',
        messageInfo: 'Destination ajoutée et brouillon enregistré avec succès.',
      });
      navigate(
        { pathname: nextPath, search: location.search },
        { replace: true }
      );
      return true;
    } catch (error) {
      setMessage({
        open: true,
        color: 'red',
        messageInfo: error?.message || 'Erreur lors de la création du brouillon.',
      });
      return false;
    } finally {
      setLoadingBackdrop(false);
      setDisabled(false);
    }
  };

  const handleSave = async (action) => { // enregistrer la feuille de temps
    setLoadingBackdrop(true);
    setDisabled(true);
    
    try {
    const dataToSend = buildTimesheetPayload();
    const IsValid = validateTimesheetForm(dataToSend, {
      requireTransport: action !== 'validate',
    });
    
    if (!IsValid.valid) {
        setMessage({ open: true, color: 'red', messageInfo: IsValid.message });
        setDisabled(false);
        return;
    }

    const result = await TimeSheetMissionService.saveTimesheetMission(dataToSend);
    if(result && result.status){
      setMessage({ open: true, color: 'green', messageInfo: 'La timesheet a été enregistrée avec succès.' });
      if(action === 'validate'){
        const savedTimesheetId = Number(result?.data?.mission_timesheet_id ?? mission_timesheet_id ?? 0);
        if (savedTimesheetId > 0) {
          return savedTimesheetId;
        }
        setMessage({
          open: true,
          color: 'red',
          messageInfo: "L'enregistrement a reussi mais l'identifiant de la timesheet est introuvable."
        });
        return false;
      }
      handleCloseDialog();
    } else {
      setMessage({ open: true, color: 'red', messageInfo: result.message || 'Une erreur est survenue lors de l\'enregistrement de la feuille de temps.' });
      setDisabled(false);
    }

    } catch (error) {
      setMessage({ open: true, color: 'red', messageInfo: error.message });
      return false;
    }
    finally {
      setLoadingBackdrop(false);
      setDisabled(false);
    }
  };

  const handleOpenDialogTransport = (selectedDestinationDetails = [], groupedDestinations = []) => { // ouvrir le dialog d'assignation des voitures
    const selectedList = groupedDestinations.length
      ? groupedDestinations.flatMap((group) => group?.destinations || [])
      : selectedDestinationDetails;
    const isEdit = selectedList.some((destination) => hasAssignedTransport(destination));
    setDialogTransport((prev) => ({
      ...prev,
      open: true,
      agenda_id: agenda_id,
      isEdit,
      selectedDestinations: groupedDestinations.length ? groupedDestinations : selectedDestinationDetails,
      groupedDestinations,
    }));
  };

  const handleAssignVehicleClick = () => { // gérer l'assignation des véhicules
    const destinationDetails = buildSelectedDestinationDetails(selectedKeys);
    if (!destinationDetails.length) {
      setSelectionWarningDialog({
        open: true,
        message: "Veuillez selectionner au moins une ligne avant d'assigner une voiture.",
      });
      return;
    }
    const groupedDestinations = groupDestinationsByTransportType(destinationDetails);
    handleOpenDialogTransport(destinationDetails, groupedDestinations);
  };

  const handleOpenSolicitationDialog = (selectedDestinationDetails = []) => {
    setDialogSolicitation({
      open: true,
      selectedDestinations: selectedDestinationDetails,
    });
  };

  const handleSolicitationClick = () => {
    const destinationDetails = buildSelectedDestinationDetails(selectedKeys);
    if (!destinationDetails.length) {
      setSelectionWarningDialog({
        open: true,
        message: "Veuillez selectionner au moins une ligne avant de solliciter une voiture.",
      });
      return;
    }
    handleOpenSolicitationDialog(destinationDetails);
  };

  const handleCloseAssignVehicleDialog = () => { // fermer le dialog d'assignation des voitures
    setDialogTransport((prev) => ({
      ...prev,
      open: false,
      agenda_id: null,
      selectedDestinations: [],
      groupedDestinations: [],
    }));
  };

  const handleCloseSolicitationDialog = () => {
    setDialogSolicitation({
      open: false,
      selectedDestinations: [],
    });
  };

  useEffect(() => {
    if (!openTransportDialog || autoTransportDialogOpened || !canManageTransport) {
      return;
    }
    if (!Array.isArray(destinations) || destinations.length === 0) {
      return;
    }

    const selectableDestinations = destinations.filter(
      (destination) => !['AVION', 'BATEAU'].includes(destination?.transport_type)
    );
    if (!selectableDestinations.length) {
      return;
    }

    const destinationDetails = buildDestinationDetails(selectableDestinations);
    const groupedDestinations = groupDestinationsByTransportType(destinationDetails);
    handleOpenDialogTransport(destinationDetails, groupedDestinations);
    setAutoTransportDialogOpened(true);
  }, [
    openTransportDialog,
    autoTransportDialogOpened,
    canManageTransport,
    destinations,
  ]);

  useEffect(() => {
    if (!openSolicitationDialog || autoSolicitationDialogOpened || !canManageTransport) {
      return;
    }
    if (!Array.isArray(destinations) || destinations.length === 0) {
      return;
    }

    const selectableDestinations = destinations.filter(
      (destination) => !['AVION', 'BATEAU'].includes(destination?.transport_type)
    );
    if (!selectableDestinations.length) {
      return;
    }

    const destinationDetails = buildDestinationDetails(selectableDestinations);
    handleOpenSolicitationDialog(destinationDetails);
    setAutoSolicitationDialogOpened(true);
  }, [
    openSolicitationDialog,
    autoSolicitationDialogOpened,
    canManageTransport,
    destinations,
  ]);

  const getLocationParts = (destination, type = 'depart') => {
      const regionKey = type === 'depart' ? 'region_depart' : 'region_arrivee';
      const districtKey = type === 'depart' ? 'district_depart' : 'district_arrivee';
      const communeKey = type === 'depart' ? 'commune_depart' : 'commune_arrivee';
      const fokontanyKey = type === 'depart' ? 'fokontany_depart' : 'fokontany_arrivee';
      const lieuKey = type === 'depart' ? 'lieu_depart' : 'lieu_arrivee';
      const hasStructuredData =
        !isEmpty(destination[regionKey]) ||
        !isEmpty(destination[districtKey]) ||
        !isEmpty(destination[communeKey]) ||
        !isEmpty(destination[fokontanyKey]) ||
        !isEmpty(destination[lieuKey]);
  
      if (hasStructuredData) {
        return {
          region: destination[regionKey] ?? '',
          district: destination[districtKey] ?? '',
          commune: destination[communeKey] ?? '',
          fokontany: destination[fokontanyKey] ?? '',
          lieu: destination[lieuKey] ?? '',
        };
      }
  
      const fallback = type === 'depart' ? formatDepart(destination) : formatArrivee(destination);
      return {
        region: fallback,
        district: '',
        commune: '',
        fokontany: '',
        lieu: '',
      };
    };

  const handleOpenDestinationView = (destination, index = null) => {
    if (!destination) {
      return;
    }
    const canEdit = missionTimesheetState !== 'CONFIRMED' && missionTimesheetState !== 'REFUSED';
    setViewDialog({ open: true, destination, index, editable: canEdit });
  };

  const handleCloseDestinationView = () => {
    setViewDialog({ open: false, destination: null, index: null, editable: false });
  };

  const handleOpenLocationModal = () => {
    if (!destinations.length) {
      setMessage({ open: true, color: 'error', messageInfo: 'Aucune destination à afficher.' });
      return;
    }
    const index = viewDialog.index ?? 0;
    const destination = destinations[index] || destinations[0];
    handleOpenDestinationView(destination, index);
  };

    const interactiveCellSx = {
      textAlign: "center",
      fontSize: "12px",
      cursor: "pointer",
    };

    const handleValidate = async () => { // validation de la timesheet
      setLoadingBackdrop(true);
    const savedTimesheetId = await handleSave('validate');
    if (!savedTimesheetId) {
      setLoadingBackdrop(false);
      return;
    }
    setLoadingBackdrop(false);
    setConfirmDialog({ // ouvrir la dialogue de confirmation de validation 
      open: true,
      mission_planning_id: mission_planning_id || null,
      agenda_id: agenda_id || null,
      mission_timesheet_id: savedTimesheetId
    });
  };

  const handleConfirmTimesheetValidation = async () => {
    setLoadingBackdrop(true);
    setDisabled(true);
    try {
        const timesheetIdToValidate = confirmDialog?.mission_timesheet_id || mission_timesheet_id;
        if (!timesheetIdToValidate) {
          setMessage({
            open: true,
            color: 'red',
            messageInfo: "Identifiant de timesheet manquant pour la validation."
          });
          setDisabled(false);
          return false;
        }
        const validationTimesheet = await TimeSheetMissionService.validateTimesheetMission(
        timesheetIdToValidate,
        mission_planning_id,
        agenda_id
      );

      if (!validationTimesheet || !validationTimesheet.status) {
        setMessage({
          open: true,
          color: 'red',
          messageInfo:
            validationTimesheet?.message ||
            "Une erreur est survenue lors de la validation de la timesheet."
        });
        setDisabled(false);
        return false;
      }
      setMissionTimesheetState('CONFIRMED');
      setMessage({
        open: true,
        color: 'green',
        messageInfo: 'Timesheet validee avec succes.'
      });
      return true;
    } catch (error) {
      setMessage({ open: true, color: 'red', messageInfo: error.message });
      return false;
    } finally {
      setLoadingBackdrop(false);
      setDisabled(false);
    }
  };

  return (
       <>
            <Dialog
            open={true}
            onClose={handleCloseDialog}
            fullScreen
            aria-labelledby="form-dialog-title"
            aria-describedby="alert-dialog-description"
            className={classes.dialog}
      >
        <DialogTitle id="form-dialog-title"><CalendarMonthIcon/> Validation Timesheet de la mission - {mission?.reference}</DialogTitle>
        <DialogContent dividers>
        <DialogContentText  sx={{fontSize: '12px', color: '#8e9194ff'}}>
              <Message open={open} handleClose={handleCloseMessage} color={color} messageInfo={messageInfo} />
              <LoadingSimpleBackdrop openLoadingSimpleBackdrop={loadingBackdrop} />
                {missionTimesheetState === "CONFIRMED" ? (
                            <Chip
                              label="Timesheet validé"
                              color="success"
                              variant="contained"
                              style={{ marginBottom: "10px" }}
                            />
                          ) : missionTimesheetState === "REFUSED" ? (
                            <Chip
                              label="Timesheet refusé"
                              color="error"
                              variant="contained"
                              style={{ marginBottom: "10px" }}
                            />
                          ) : null}
              <Grid item xs={12} sm={12} md={12}>
                          {mission?.destinations?.length > 0 && (
                                       <Table sx={{color: '#8e9194ff', marginTop: '-8px'}} size="small">
                                        <TableRow>
                                          <TableCell sx={{ minWidth: '188px', fontWeight: 'bold' }}>Destination(s)</TableCell>
                                          <TableCell sx={{ minWidth: '180px', fontWeight: 'bold' }}>Date de début</TableCell>
                                          <TableCell sx={{ minWidth: '180px', fontWeight: 'bold' }}>Date de fin</TableCell>
                                          <TableCell sx={{ minWidth: '180px', fontWeight: 'bold' }}>Missionnaire(s)</TableCell>
                                        </TableRow>
                                        <TableBody sx={{color: '#8e9194ff'}}>
                                          {mission?.destinations?.map((destination, index) => (
                                            <TableRow key={index}>
                                              <TableCell sx={{color: '#8e9194ff'}}>{destination.lieu} / {destination.direction}</TableCell>
                                              <TableCell sx={{color: '#8e9194ff'}}>{`${new Date(destination.date_begin).toLocaleDateString()}`}</TableCell>
                                              <TableCell sx={{color: '#8e9194ff'}}>{`${new Date(destination.date_end).toLocaleDateString()}`}</TableCell>
                                              <TableCell sx={{color: '#8e9194ff'}}>{destination.missionnaries?.map(m => m.name)?.join(', ')}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                  )}
                          </Grid>
                 <Alert severity="info" size="small" sx={{ fontSize: "12px", height: "40px", marginTop: "4px" }}>
                          Les modifications apportées aux lignes d’agenda <strong>(ajout ou retrait)</strong> n’affectent pas l’agenda affiché dans le TDR. Elles servent uniquement à optimiser la timesheet et le calcul du carburant.
                      </Alert>
                      {
                        agendas.length !==0 ? (
                      agendas.map((agenda, index) => (
                 <fieldset className={classes.fieldset}>
                        <legend>
                          &nbsp;&nbsp;
                          {`Agenda ${ agendas?.length > 1 ? (agendas[index].mission_planning_destination_id	!= null ? 'mission ' + (index + 1) : (index + 1)) : ''} : ${agenda.reference}`}
                          &nbsp; &nbsp;
                        </legend>
                          <TabContext value={value}>
                      <TabPanel value="1" sx={{ pr: 0 }}>
                           {
                            canManageTransport && ( 
                         <Grid container spacing={2} >
                          <Grid item xs={12} sm={8} md={8} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                           <Button color='info' variant="contained" startIcon={<AddIcon />} size="small" onClick={() => handleOpenDestDialog(agenda.mission_planning_agenda_id)}>
                             Ajouter une ligne
                          </Button>
                         
                          <Button variant="contained"  sx={{marginLeft: "10px"}} size='small' color='success' startIcon={<DirectionsCarIcon />} onClick={handleAssignVehicleClick}>
                                Assignation des voiture(s)
                              </Button>
                             
                           </Grid>
                           <Grid item xs={12} md={4} sm={4} className={classes.rightActions} style={{ paddingLeft: 0, paddingRight: 0 }}>
                            <>  <Button color='secondary' variant="contained" startIcon={<CarRentalIcon />} size="small" 
                           onClick={() => setValue('2')}
                          >
                            Paramétrage des locations
                          </Button>
                           <Button variant="contained" size='small' color='warning' startIcon={<DirectionsCarIcon />} onClick={handleSolicitationClick}>
                                Solliciter une voiture
                              </Button>
                              </>
                              </Grid>
                           </Grid>
                         )}
                        <TableContainer sx={{maxWidth: "92vw", width: "100%", height: "580px", overflowX: "auto", overflowY: "auto"}}>
                          <Table>
                                                        <TableHead>
                                  <TableRow sx={{ "& th": stickyHeaderCellSx }}>
                                    {/* {canManageTransport && (
                                      <TableCell rowSpan={2} padding="checkbox" sx={{ minWidth: '10px'}}>
                                        <CheckBoxTimesheet {...getHeaderCheckboxProps()} />
                                      </TableCell>
                                    )} */}
                                    {canManageTransport && <TableCell sx={{ minWidth: '8px'}} rowSpan={2}></TableCell>}
                                    <TableCell rowSpan={2} sx={{ maxWidth: '100px', textAlign: "center" }}>Date(s)</TableCell>
                                    <TableCell rowSpan={2} sx={{ maxWidth: '80px', textAlign: "center" }}>Heure départ</TableCell>
                                    <TableCell rowSpan={2} sx={{ maxWidth: '80px', textAlign: "center" }}>Heure arrivée</TableCell>
                                    <TableCell colSpan={5} sx={{ minWidth: '210px', textAlign: "center"}}>Lieu de départ</TableCell>
                                    <TableCell colSpan={5} sx={{ minWidth: '210px', textAlign: "center"}}>Lieu d'arrivée</TableCell>
                                    <TableCell rowSpan={2} sx={{ minWidth: '60px', textAlign: "center" }}>Type</TableCell>
                                    <TableCell rowSpan={2} sx={{ minWidth: '130px', textAlign: "center" }}>Financement</TableCell>
                                    <TableCell rowSpan={2} sx={{ minWidth: '130px', textAlign: "center" }}>Estimation Kilométrage</TableCell>
                                    <TableCell rowSpan={2} sx={{ minWidth: '220px', textAlign: "center" }}>Moyen de transport <br/>souhaité <Bullred/></TableCell>
                                    <TableCell rowSpan={2} sx={{ minWidth: '140px', textAlign: "center" }}>Immatriculation(s)</TableCell>
                                    <TableCell rowSpan={2} sx={{ minWidth: '140px', textAlign: "center" }}>Consommation en l</TableCell>
                                    <TableCell rowSpan={2} sx={{ minWidth: '140px', textAlign: "center" }}>Prix unitaire en Ar</TableCell>
                                    <TableCell rowSpan={2} sx={{ minWidth: '140px', textAlign: "center" }}>Carburant en Ar</TableCell>
                                    <TableCell rowSpan={2} sx={{ minWidth: '120px', textAlign: "center" }}>Description montant en charge</TableCell>
                                    <TableCell rowSpan={2} sx={{ minWidth: '100px', textAlign: "center" }}>Mode de paiement</TableCell>
                                    <TableCell rowSpan={2} sx={{ minWidth: '100px', textAlign: "center" }}>Autre Montant en charge en Ar</TableCell>
                                    <TableCell rowSpan={2} sx={{ minWidth: '100px', textAlign: "center" }}>Nombre de nuitées</TableCell>
                                    <TableCell rowSpan={2} sx={{ minWidth: '400px', textAlign: "center" }}>Observation</TableCell>
                                  </TableRow>
                                  <TableRow sx={{ "& th": stickySubHeaderCellSx }}>
                                     <TableCell sx={{ minWidth: '70px', textAlign: "center"}} className={classes.tdBordered}>Région</TableCell>
                                      <TableCell sx={{ minWidth: '70px', textAlign: "center" }}>District</TableCell>
                                      <TableCell sx={{ minWidth: '70px', textAlign: "center" }}>Commune</TableCell>
                                      <TableCell sx={{ minWidth: '70px', textAlign: "center" }}>Fokontany</TableCell>
                                      <TableCell sx={{ minWidth: '90px', textAlign: "center"}}>Lieu</TableCell>
                                      <TableCell sx={{ minWidth: '70px', textAlign: "center"}} className={classes.tdBordered}>Région</TableCell>
                                      <TableCell sx={{ minWidth: '70px', textAlign: "center" }}>District</TableCell>
                                      <TableCell sx={{ minWidth: '70px', textAlign: "center" }}>Commune</TableCell>
                                      <TableCell sx={{ minWidth: '70px', textAlign: "center" }}>Fokontany</TableCell>
                                      <TableCell sx={{ minWidth: '90px', textAlign: "center"}} className={classes.tdBorderedRight}>Lieu</TableCell>
                                  </TableRow>
                                                              </TableHead>
                                  
                  <TableBody sx={{
                    "& .MuiTableCell-body": {
                      fontSize: "12px",
                    },
                  }}>
                    {loadingDestinations ? (
                      Array.from({ length: 6 }).map((_, skeletonIndex) => (
                        <TableRow key={`skeleton-${skeletonIndex}`}>
                          {Array.from({ length: tableSkeletonColumns }).map((__, cellIndex) => (
                            <TableCell key={`skeleton-cell-${skeletonIndex}-${cellIndex}`}>
                              <Skeleton variant="rectangular" height={24} />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                    <>
                    {destinations.map((dest, index) => {
                       const departParts = getLocationParts(dest, 'depart');
                      const arriveeParts = getLocationParts(dest, 'arrivee');
                      const {
                        informationsLocation,
                        detailsDestination,
                        detailsTransports,
                      } = calculerDetailsCarburantDestination(dest, locationsParId);
                      const afficherParTransport = detailsTransports.length > 0;
                      const isExternalCarpooling = isExternalCarpoolingDestination(dest);
                      const hasRent = valeurEstRenseignee(informationsLocation.mission_rent_id);
                      const rentDisplayName =
                        informationsLocation.rent_type || dest?.transport_type || 'Location';
                      const rentPaymentMode = String(
                        informationsLocation.rent_paiement || ''
                      ).toLowerCase();
                      const isFlatRent = ['flat', 'forfait', 'forfaitaire'].includes(rentPaymentMode);
                      const repeatedRentCount = hasRent
                        ? (rentOccurrencesById[informationsLocation.mission_rent_id] || 0)
                        : 0;
                      const isRepeatedFlatRent = isFlatRent && repeatedRentCount > 1;
                      const rentPaymentLabel = hasRent
                        ? (isRepeatedFlatRent ? 'Forfaitaire unique' : (isFlatRent ? 'Forfaitaire' : 'Paiement par jour'))
                        : null;
                      const rentAmountToDisplay = indicesMontantsJournaliersMasques.has(index)
                        ? null
                        : informationsLocation.rent_amount;
                      const nightDateKey = getNightDateKey(dest?.date_begin, index);
                      const nightGroup = nightDistributionMeta.groups.get(nightDateKey);
                      const canEditNightNumber =
                        canManageTransport &&
                        nightGroup?.lastIndex === index;
                      return (
                        <TableRow key={index}>
                        {/* {canManageTransport && (
                          <TableCell padding="checkbox" sx={{ minWidth: '8px'}}>
                            <CheckBoxTimesheet {...getRowCheckboxProps(dest)} />
                          </TableCell>
                        )} */}
                        {canManageTransport && <TableCell sx={{ minWidth: '10px'}}>
                          <IconButton aria-label="modifier la ligne" size="small" color="info" onClick={() => handleOpenDestinationView(dest, index, true)} sx={{backgroundColor: "#e6f7ff", borderRadius: "20px"}}>
                                                      <EditIcon fontSize="inherit"/>
                                                    </IconButton>
                            <IconButton aria-label="delete" size="small" color="error" sx={{backgroundColor: "#ffebe6", borderRadius: "20px"}}
                             onClick={() => setRemoveDialog({ open: true, agenda_id: agenda.mission_planning_agenda_id,
                            mission_destination_id: dest.mission_destination_id || null, 
                             mission_timesheet_destination_id: dest.mission_timesheet_destination_id || null,
                             rowIndex: index })}
                             >
                            <CloseIcon fontSize="inherit" />
                          </IconButton>
                          </TableCell>}
                        <TableCell sx={{ minWidth: '40px', fontSize: "12px"}} className={classes.tdBordered}>{formatDate(dest.date_begin) == formatDate(dest.date_end) ? formatDate(dest.date_begin) : (formatDate(dest.date_begin) + " - " + formatDate(dest.date_end))}</TableCell>
                        <TableCell sx={{textAlign: "center", fontSize: "12px"}}>{dest.heure_begin}</TableCell>
                        <TableCell sx={{textAlign: "center", fontSize: "12px"}}>{dest.heure_end}</TableCell>
                        <TableCell sx={{ ...interactiveCellSx, borderLeft: "1px solid #e6e6e6" }} >{departParts.region}</TableCell>
                        <TableCell sx={interactiveCellSx} >{departParts.district}</TableCell>
                        <TableCell sx={interactiveCellSx} >{departParts.commune}</TableCell>
                        <TableCell sx={interactiveCellSx} >{departParts.fokontany}</TableCell>
                        <TableCell sx={interactiveCellSx} >{departParts.lieu || dest.lieu_depart || dest.direction_location || ''}</TableCell>
                        <TableCell sx={{ ...interactiveCellSx, borderLeft: "1px solid #e6e6e6" }} >{arriveeParts.region}</TableCell>
                        <TableCell sx={interactiveCellSx} >{arriveeParts.district}</TableCell>
                        <TableCell sx={interactiveCellSx} >{arriveeParts.commune}</TableCell>
                        <TableCell sx={interactiveCellSx} >{arriveeParts.fokontany}</TableCell>
                        <TableCell sx={interactiveCellSx} >{arriveeParts.lieu || dest.lieu_arrivee || dest.direction_location || ''}</TableCell>
                        <TableCell sx={{ borderLeft: "1px solid #e6e6e6"}}>{dest.type === "DM" ? <Chip icon={<CardTravelIcon />} label="Mission" variant="outlined" /> : <Chip icon={<TimeToLeaveIcon />} style={{ fontSize: "12px" }} label="Délai de route" variant="outlined"/>}</TableCell>
                        <TableCell>{ dest.code_financement_mis}</TableCell>
                        <TableCell sx={{minWidth: '130px', textAlign: "center"}}><FormControl fullWidth variant="outlined" size="small">
                         {
                          missionTimesheetState !== 'CONFIRMED' && missionTimesheetState !== 'REFUSED' ? (
                           <TextField
                              id="outlined-number"
                              label="Km"
                              type="number"
                              size="small"
                              onChange={(e) => handleChangeKm(e, index)}
                              value={dest?.distance_km ?? ""}
                              className={dest?.kmGapExceeded ? classes.kmInputError : ''}
                            />) : (
                             <label style={{fontWeight: 'bold'}}>{dest?.distance_km || 0} </label>
                          )
                         }
                          </FormControl>
                         </TableCell>
                        <TableCell sx={{ minWidth: '220px', textAlign: "center" }}>
                          {
                            missionTimesheetState !== 'CONFIRMED' && missionTimesheetState !== 'REFUSED' ? (
                        <Autocomplete
                              disablePortal
                              id="combo-box-demo"
                              options={transportMeans}
                              getOptionLabel={(option) => option.label}
                              isOptionEqualToValue={(option, selectedValue) => {
                                const optionRentId = option?.mission_rent_id;
                                const selectedRentId = selectedValue?.mission_rent_id;
                                if (valeurEstRenseignee(optionRentId) || valeurEstRenseignee(selectedRentId)) {
                                  return String(optionRentId ?? '') === String(selectedRentId ?? '');
                                }
                                return option?.value === selectedValue?.value;
                              }}
                              size="small"
                                sx={{
                                "& .MuiAutocomplete-option": {
                                  fontSize: "13px",
                                },
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  sx={{
                                    "& .MuiInputBase-input": {
                                      fontSize: "13px",
                                    },
                                  }}
                                />
                              )}
                              value={
                                (() => {
                                  const missionRentId = obtenirIdLocationDestination(dest);
                                  if (valeurEstRenseignee(missionRentId)) {
                                    return transportMeans.find(
                                      (transport) => String(transport?.mission_rent_id) === String(missionRentId)
                                    ) || null;
                                  }
                                  return transportMeans.find(
                                    (transport) => !valeurEstRenseignee(transport?.mission_rent_id)
                                      && transport?.value === dest?.transport_type
                                  ) || null;
                                })()
                              }
                              onChange={(_, newValue) => handleChangeFormValues('transport_type', newValue ? newValue : {}, index)}
                            />
                            ) : (
                              <label style={{fontWeight: 'bold'}}>{transportMeans.find(t => t.value === dest?.transport_type)?.label || ""}</label>
                            )
                          }
                        </TableCell>
                        <TableCell sx={{textAlign: 'center', borderLeft: "1px solid #e6e6e6"}}>
                          {afficherParTransport
                            ? detailsTransports.map(({ transport }, idx) => (
                                <TableRow key={transport?.missionary_timesheet_mobility_id || transport?.transport_id || idx} style={{marginBottom: '4px'}}>
                                  <TableCell>
                                    {transport?.vhc_imm ||
                                      (valeurEstRenseignee(transport?.vhc_id)
                                        ? `Location`
                                        : rentDisplayName)}
                                    {isExternalCarpooling && (
                                      <label style={{marginLeft: '6px', color: 'orange'}}> (Covoiturage)</label>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))
                            : dest?.vhc_imm || ''}
                        </TableCell>
                        <TableCell sx={{textAlign: "center"}}>
                          {isExternalCarpooling
                            ? ""
                            : afficherParTransport
                              ? detailsTransports.map(({ transport, details }, idx) => (
                                  <TableRow key={transport?.missionary_timesheet_mobility_id || transport?.transport_id || idx} style={{marginBottom: '4px'}}>
                                    <TableCell>{details.consommation
                                      ? formatWithThousandSeparator(details.consommation, { fallback: '' })
                                      : ""}
                                    </TableCell>
                                  </TableRow>
                                ))
                              : detailsDestination?.consommation
                                ? formatWithThousandSeparator(detailsDestination.consommation, { fallback: '' })
                                : ""}
                        </TableCell>
                        <TableCell sx={{textAlign: "center"}}>
                          {isExternalCarpooling
                            ? ""
                            : afficherParTransport
                              ? detailsTransports.map(({ transport, details }, idx) => (
                                  <TableRow key={transport?.missionary_timesheet_mobility_id || transport?.transport_id || idx} style={{marginBottom: '4px'}}>
                                    <TableCell>{details.prixUnitaire
                                      ? formatWithThousandSeparator(details.prixUnitaire, { fallback: '' })
                                      : ""}
                                    </TableCell>
                                  </TableRow>
                                ))
                              : detailsDestination?.prixUnitaire
                                ? formatWithThousandSeparator(detailsDestination.prixUnitaire, { fallback: '' })
                                : ""}
                        </TableCell>
                        <TableCell sx={{textAlign: "center"}}>
                          {isExternalCarpooling
                            ? ""
                            : afficherParTransport
                              ? detailsTransports.map(({ transport, details }, idx) => (
                                  <TableRow key={transport?.missionary_timesheet_mobility_id || transport?.transport_id || idx} style={{marginBottom: '4px'}}>
                                    <TableCell>{details.montantCarburant
                                      ? formatWithThousandSeparator(details.montantCarburant, { fallback: '' })
                                      : ""}
                                    </TableCell>
                                  </TableRow>
                                ))
                              : detailsDestination?.montantCarburant
                                ? formatWithThousandSeparator(detailsDestination.montantCarburant, { fallback: '' })
                                : ""}
                        </TableCell>
                        <TableCell sx={{textAlign: "center"}}>
                          <label>{isExternalCarpooling ? "" : hasRent ? "Prix Location" : null}</label>
                        </TableCell>
                       
                        <TableCell  >
                          {!isExternalCarpooling && rentPaymentLabel ? (
                            <Box>
                              <label style={{color: "green"}}>{rentPaymentLabel}</label>
                            </Box>
                          ) : null}
                          </TableCell>
                           <TableCell sx={{ minWidth: '120px', textAlign: "center" }}>
                          {!isExternalCarpooling && hasRent ? (
                            <Box>
                              <label>{formatWithThousandSeparator(rentAmountToDisplay, { fallback: '' })}</label>
                              {isRepeatedFlatRent ? (
                                <Box sx={{ fontSize: '11px', color: '#5f6b7a', lineHeight: 1.25, mt: 0.5, fontStyle: 'italic' }}>
                                  {`Montant global : ${rentDisplayName}`}
                                </Box>
                              ) : null}
                            </Box>
                          ) : null}
                        </TableCell>
                        <TableCell sx={{textAlign: "center", borderLeft: "1px solid #e6e6e6"}}>
                          {canManageTransport ? (
                            <TextField
                              type="number"
                              size="small"
                              value={dest?.night_number ?? 0}
                              disabled={!canEditNightNumber || loadingDestinations || disabled}
                              onChange={(event) => handleChangeNightNumber(event.target.value, index)}
                              onFocus={(event) => event.target.select()}
                              onBlur={() => handleBlurNightNumber(index)}
                              inputProps={{ min: 0, step: 1 }}
                              sx={{
                                minWidth: '80px',
                                '& input': {
                                  textAlign: 'center',
                                  fontWeight: 'bold',
                                  color: 'purple',
                                },
                              }}
                            />
                          ) : (
                            <label style={{fontWeight: 'bold', color: 'purple'}}>
                              {dest?.night_number ?? 0}
                            </label>
                          )}
                        </TableCell>
                         <TableCell sx={{width: '400px', borderLeft: "1px solid #e6e6e6"}}>
                          {
                            missionTimesheetState !== 'CONFIRMED' && missionTimesheetState !== 'REFUSED' ? (
                          
                          <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            value={dest?.observation || ""}
                            placeholder='votre commentaire ici ...'
                            multiline
                            rows={2}
                            sx={{
                                    "& .MuiInputBase-input": {
                                      fontSize: "13px",
                                    },
                                  }}
                            onChange={(e) => handleChangeFormValues('observation', e.target.value, index)}
                            />
                            ) : (
                              <label>{dest?.observation || ""}</label>
                            )
                          }
                        </TableCell>
                      </TableRow> 
                    )})}
                      <TableRow>
                      <TableCell colSpan={totalLabelColumnSpan} sx={{ fontWeight: 'bold', textAlign: 'right' }}>Total Général :</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                        {formatWithThousandSeparator(destinations.reduce((sum, item) => sum + (parseFloat(item.distance_km) || 0), 0), { fallback: '' })}
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                        {formatWithThousandSeparator(destinations.reduce((sum, item) => {
                          const isExternalCarpoolingRow = isExternalCarpoolingDestination(item);
                          if (isExternalCarpoolingRow) {
                            return sum;
                          }
                          const { detailsDestination } = calculerDetailsCarburantDestination(
                            item,
                            locationsParId
                          );
                          return sum + convertirEnNombre(detailsDestination?.consommation, 0);
                        }, 0), { fallback: '' })}
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                        {formatWithThousandSeparator(destinations.reduce((sum, item) => {
                          const isExternalCarpoolingRow = isExternalCarpoolingDestination(item);
                          if (isExternalCarpoolingRow) {
                            return sum;
                          }
                          const { detailsDestination } = calculerDetailsCarburantDestination(
                            item,
                            locationsParId
                          );
                          return sum + convertirEnNombre(detailsDestination?.montantCarburant, 0);
                        }, 0), { fallback: '' })}
                      </TableCell>
                      <TableCell colSpan={3}></TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                        {formatWithThousandSeparator(destinations.reduce((sum, item) => sum + (parseFloat(item.night_number) || 0), 0), { fallback: '' })}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                    </>
                    )}
                  </TableBody> 
                           </Table>
                          </TableContainer>
                          </TabPanel>
                      <TabPanel value="2"> 
                          <RentManagement setMessage={setMessage} setValue={setValue} setTransportMeans={setTransportMeans} mission_planning_agenda_id={agenda_id} />
                        </TabPanel>
                          </TabContext>
                  </fieldset>
                        ))) : (
                          <Alert severity="info" size="small" sx={{ marginTop: "1%", fontSize: "12px" }}>
                          Aucun agenda n'est associé à cette mission.
                      </Alert>
                        )
                      }
        </DialogContentText>

                </DialogContent>
                <DialogActions>
                   <Box container item xs={6} spacing={2} display="flex" justifyContent="" sx={{ marginTop: "5px", marginBottom: "5px" }}>
                                             <span style={{color: "red", marginRight: "24vw"}} ><strong> Veuillez défiler horizontalement le tableau pour voir toutes les colonnes.</strong>
                  </span>  
                                </Box>
                             
             {
              missionTimesheetState !== 'CONFIRMED' && missionTimesheetState !== 'REFUSED' ? ( 
             <Box container item xs={6} spacing={2} display="flex" justifyContent="flex-end" sx={{ marginTop: "5px", marginBottom: "5px" }}>

                     <Button variant="contained"
                    size="medium"
                    style={{ marginRight: '30px', }}
                    onClick={() => handleCloseDialog()}
                    autoFocus
                    color="grey"
                  >
                    Fermer
                  </Button>
                  
                    <Button  
                    style={{ marginRight: '30px' }}
                    color="error"
                    variant="contained"
                    loading={loadingButton}
                    disabled={loadingButton}
                    onClick={() => setDialogRefus({ open: true, timesheetId: mission_timesheet_id })}
                  >
                    Refuser
                  </Button>
                  <Button
                    variant="contained"
                    color="info"
                    size="medium"
                    style={{ marginRight: '30px' }}
                    onClick={() => handleSave('save')}
                    autoFocus
                    disabled={loadingButton}
                  >
                    Enregistrer
                  </Button>
                      <Button
                        
                        color="success"
                        variant="contained"
                        loading={loadingButton}
                        disabled={loadingButton}
                        onClick={() => handleValidate()}
                      >
                        {"Valider"}
                      </Button>
        
                                  </Box>
            ) : (
              <Box container item xs={12} spacing={2} display="flex" justifyContent="flex-end" sx={{ marginTop: "5px", marginBottom: "5px" }}>
                      <Button variant="contained"
                    size="medium"
                    style={{ marginRight: '30px', }}
                    onClick={() => handleCloseDialog()}
                    autoFocus
                    color="error"

                  >
                    Fermer
                  </Button>
              </Box>
            )
          }
                </DialogActions>
            </Dialog>
              <DestinationDialog
                destDialog={destDialog}
                setDestDialog={setDestDialog}
                setMessage={setMessage}
                fetchDestinations={fetchDestinations}
                onAddDestination={handleAddDestination}
              />
            {/* // Dialog de confirmation de suppression d'une destination */}
           <RemoveDestinationDialog removeDialog={removeDialog} setRemoveDialog={setRemoveDialog} setMessage={setMessage} setOpenLoadingSimpleBackdrop={setLoadingBackdrop} 
           setDestinations={setDestinations} fetchDestinations={fetchDestinations} />
           <DestinationViewDialog open={viewDialog.open} destination={viewDialog.destination} onClose={handleCloseDestinationView} editable={viewDialog.editable} setDestinations={setDestinationsWithNightDistribution} />
           <Dialog
             open={selectionWarningDialog.open}
             onClose={() => setSelectionWarningDialog({ open: false, message: '' })}
           >
             <DialogTitle>Information</DialogTitle>
             <DialogContent>
               <DialogContentText>
                 {selectionWarningDialog.message}
               </DialogContentText>
             </DialogContent>
             <DialogActions>
               <Button onClick={() => setSelectionWarningDialog({ open: false, message: '' })} autoFocus>
                 OK
               </Button>
             </DialogActions>
           </Dialog>
                  {/* Dialog d'attribution de véhicule */}
                  <MissionAssignVehicle
                    openDialog={dialogTransport.open}
                    closeDialog={handleCloseAssignVehicleDialog}
                    onSuccessSubmit={fetchDestinations}
                    isEdit={dialogTransport.isEdit}
                    missionData={mission}
                    selectedDestinations={dialogTransport.selectedDestinations}
                    groupedDestinations={dialogTransport.groupedDestinations}
                    mission_timesheet_user_id ={missionTimesheetUserId}
                  /> 
                  <MissionVehicleSolicitation
                    open={dialogSolicitation.open}
                    onClose={handleCloseSolicitationDialog}
                    missionId={mission_planning_id}
                    loading={loadingBackdrop}
                    agendaId={agenda_id}
                    setMessage={setMessage}
                    selectedDestinations={dialogSolicitation.selectedDestinations}
                  />
       {/* Dialog de refus de feuille de temps */}
       <TimesheetRefuse 
           setOpenLoadingSimpleBackdrop={setLoadingBackdrop}
           open={dialogRefus.open}
           timesheetId={dialogRefus.timesheetId}
            mission_planning_id={mission_planning_id}
            mission_planning_agenda_id={agenda_id}
           onClose={() => {
            setDialogRefus({ ...dialogRefus, open: false, timesheetId: null })
            handleCloseDialog()}}
       />
       <ConfirmationTransport
         openDialog={confirmDialog.open}
         mission_planning_id={mission_planning_id}
         mission_planning_agenda_id={agenda_id}
         mission_timesheet_id={confirmDialog?.mission_timesheet_id || mission_timesheet_id}
         closeDialog={() => {
            setConfirmDialog({ ...confirmDialog, open: false });
            handleCloseDialog();
           handleCloseAssignVehicleDialog();
         }}
         reference={mission?.reference}
         onConfirm={handleConfirmTimesheetValidation}
       />
       
       </>

    );
}
export default TimesheetValidate;
